import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import Layout from "../Component/Layout";
import axios from "axios";
import { MASTER_API, ORDER_API } from "../apiConfig";
import { useNavigate, useLocation } from "react-router-dom";
import { FileText } from 'lucide-react';
import { authHeaders } from "../utils/authHeaders";
/* ===================== STYLES (matches ProductPlanning.js / Pegging.js) ===================== */

const tableHeader = {
  background: "#f1f1f1",
  padding: "10px 8px",
  borderBottom: "1px solid #ccc",
  textAlign: "center",
  fontSize: "12px",
  fontWeight: 600,
  color: "#555",
  whiteSpace: "nowrap"
};

const tableCell = {
  padding: "10px 8px",
  borderBottom: "1px solid #eee",
  fontSize: "13px",
  textAlign: "center",
  color: "#444",
  wordBreak: "break-word",
  whiteSpace: "normal"
};

// Matches Pegging.js / ProductPlanning.js dropdown look
const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: 32,
    height: 32,
    fontSize: "13px",
    borderColor: "#dee2e6",
    boxShadow: "none",
    "&:hover": { borderColor: "#b3b3b3" },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 8px",
    height: "32px",
    display: "flex",
    alignItems: "center",
  }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  dropdownIndicator: (base) => ({ ...base, padding: 4 }),
  menu: (base) => ({ ...base, fontSize: "13px", zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 99999 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#1e293b"
      : state.isFocused
        ? "#f1f5f9"
        : "white",
    color: state.isSelected ? "white" : "#334155",
    cursor: "pointer",
    fontSize: "13px",
  }),
};

const pageTitle = { fontSize: 20, fontWeight: 700, color: "#1e293b", margin: 0 };

const filterLabel = {
  fontSize: 12,
  fontWeight: 600,
  marginBottom: 6,
  color: "#334155",
};

// ---------------------------------------------------------------------------
// DateTimeInput — always displays "dd-mm-yyyy HH:mm" regardless of browser /
// system locale (a native <input type="datetime-local"> can't be forced to a
// format). The value going in and out stays the ISO "yyyy-MM-ddTHH:mm" string,
// so the API params and the rest of the page are unchanged.
// The 📅 button opens the native calendar/time picker (hidden input) as a helper.
// ---------------------------------------------------------------------------
const isoToDisplay = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(iso || "");
  return m ? `${m[3]}-${m[2]}-${m[1]} ${m[4]}:${m[5]}` : "";
};

const displayToIso = (txt) => {
  const m = /^(\d{2})-(\d{2})-(\d{4}) (\d{2}):(\d{2})$/.exec(txt);
  if (!m) return null;
  const [, dd, mm, yyyy, hh, mi] = m;
  const d = new Date(+yyyy, +mm - 1, +dd, +hh, +mi);
  const valid =
    d.getFullYear() === +yyyy &&
    d.getMonth() === +mm - 1 &&
    d.getDate() === +dd &&
    +hh < 24 &&
    +mi < 60;
  return valid ? `${yyyy}-${mm}-${dd}T${hh}:${mi}` : null;
};

// Auto-inserts the "-", space and ":" while typing: 17092026 0950 -> 17-09-2026 09:50
const maskDateTime = (raw) => {
  const d = raw.replace(/\D/g, "").slice(0, 12);
  let out = d.slice(0, 2);
  if (d.length > 2) out += "-" + d.slice(2, 4);
  if (d.length > 4) out += "-" + d.slice(4, 8);
  if (d.length > 8) out += " " + d.slice(8, 10);
  if (d.length > 10) out += ":" + d.slice(10, 12);
  return out;
};

const DateTimeInput = ({ value, onChange }) => {
  const [text, setText] = useState(isoToDisplay(value));
  const pickerRef = useRef(null);

  // Keep the text in sync when the value changes from outside (e.g. cleared / picker).
  useEffect(() => {
    setText((prev) => (displayToIso(prev) === (value || null) ? prev : isoToDisplay(value)));
  }, [value]);

  const handleType = (e) => {
    const isDeleting = e.target.value.length < text.length;
    const next = isDeleting ? e.target.value : maskDateTime(e.target.value);
    setText(next);
    if (next === "") {
      onChange("");
      return;
    }
    const iso = displayToIso(next);
    if (iso) onChange(iso);
  };

  const handleBlur = () => {
    // Incomplete / invalid text -> fall back to the last valid value
    if (text && !displayToIso(text)) setText(isoToDisplay(value));
  };

  const openPicker = () => {
    const el = pickerRef.current;
    if (!el) return;
    if (typeof el.showPicker === "function") el.showPicker();
    else el.focus();
  };

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <input
        type="text"
        inputMode="numeric"
        value={text}
        onChange={handleType}
        onBlur={handleBlur}
        placeholder="dd-mm-yyyy hh:mm"
        maxLength={16}
        style={{
          width: "100%",
          height: "32px",
          border: "1px solid #dee2e6",
          borderRadius: "4px",
          padding: "0 34px 0 10px",
          fontSize: "13px",
          boxSizing: "border-box",
        }}
      />
      <button
        type="button"
        onClick={openPicker}
        aria-label="Open calendar"
        style={{
          position: "absolute",
          right: 4,
          top: 4,
          width: 24,
          height: 24,
          border: "none",
          background: "transparent",
          cursor: "pointer",
          fontSize: 14,
          padding: 0,
        }}
      >
        📅
      </button>
      {/* Hidden native picker — only used to pick a date/time, never displayed */}
      <input
        ref={pickerRef}
        type="datetime-local"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        tabIndex={-1}
        aria-hidden="true"
        style={{
          position: "absolute",
          right: 0,
          bottom: 0,
          width: 1,
          height: 1,
          opacity: 0,
          pointerEvents: "none",
        }}
      />
    </div>
  );
};

const filterCard = {
  background: "#ffffff",
  borderRadius: 8,
  padding: 15,
  boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
  marginBottom: 18,
};

// Matches Pegging.js / ProductPlanning.js compact filter row styling
const compactSelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: 30,
    fontSize: "12px",
    borderColor: "#dee2e6",
    boxShadow: "none",
    "&:hover": { borderColor: "#b3b3b3" },
  }),
  valueContainer: (base) => ({ ...base, padding: "2px 6px", flexWrap: "wrap" }),
  input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: "12px" }),
  indicatorsContainer: (base) => ({ ...base, alignSelf: "flex-start", paddingTop: 2 }),
  dropdownIndicator: (base) => ({ ...base, padding: 2 }),
  menu: (base) => ({ ...base, fontSize: "12px", zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 99999 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#1e293b"
      : state.isFocused
        ? "#f1f5f9"
        : "white",
    color: state.isSelected ? "white" : "#334155",
    cursor: "pointer",
    fontSize: "12px",
  }),
};

const compactFilterBox = {
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  borderRadius: 8,
  padding: "10px 12px",
  marginBottom: 14,
};

const compactFilterItemLabel = {
  fontSize: 11,
  fontWeight: 600,
  color: "#64748b",
  whiteSpace: "nowrap",
  letterSpacing: "0.02em",
  paddingLeft: 2,
};

/* ===================== FILTER CONFIG (same as ProductPlanning.js) ===================== */
const FILTER_FIELDS = [
  { label: "Product Type", key: "productType", dataKey: "productTypes", endpoint: "product-types" },
  { label: "Category", key: "category", dataKey: "categories", endpoint: "dynamic/category" },
  { label: "Cable Type", key: "cableType", dataKey: "cableTypes", endpoint: "cable-type" },
  { label: "Code", key: "code", dataKey: "codes", endpoint: "dynamic/code" },
  { label: "Color", key: "color", dataKey: "colours", endpoint: "colour" },
  { label: "Cores", key: "noOfCores", dataKey: "cores", endpoint: "core" },
  { label: "Cross Section", key: "nominalCrossSection", dataKey: "nominalCrossSections", endpoint: "dynamic/nominal-cross-section" },
  { label: "Std. Ref", key: "referenceOfStandard", dataKey: "referenceOfStandards", endpoint: "dynamic/reference-of-standard" },
  { label: "Voltage", key: "voltage", dataKey: "voltages", endpoint: "dynamic/voltage" },
  { label: "Construction", key: "construction", dataKey: "constructions", endpoint: "construction" },
];

// run_date/run_time can come back either as plain "YYYY-MM-DD" / "HH:MM:SS"
// strings, or as full ISO strings (e.g. "2026-09-10T00:00:00.000Z") if the
// mysql driver/timezone setting serializes DATE/TIME columns that way. Both
// are handled here instead of trusting one shape and getting "Invalid Date".
const formatRunDate = (value) => {
  if (!value) return "-";
  const str = String(value);
  const datePart = str.includes("T") ? str.split("T")[0] : str;
  const [y, m, d] = datePart.split("-");
  return y && m && d ? `${d}-${m}-${y}` : datePart;
};

const formatRunTime = (value) => {
  if (!value) return "-";
  const str = String(value);
  if (str.includes("T")) {
    // Full ISO timestamp (e.g. "2026-09-10T07:00:00.000Z") — this is UTC,
    // so it must be converted to the viewer's local time, not just have
    // the T/Z stripped off (that was showing the raw UTC clock digits,
    // which is why a 12:30 IST run was displaying as 6:00/7:00).
    const d = new Date(str);
    if (!isNaN(d.getTime())) {
      return d.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });
    }
    return str.split("T")[1].replace("Z", "").split(".")[0];
  }
  // Plain "HH:MM:SS" string — already a local wall-clock value, no
  // timezone info attached, so nothing to convert.
  return str;
};

const paginationButtonStyle = (disabled) => ({
  height: 30,
  padding: "0 10px",
  borderRadius: 4,
  border: "1px solid #dee2e6",
  fontSize: 13,
  background: disabled ? "#f1f5f9" : "#fff",
  color: disabled ? "#94a3b8" : "#334155",
  cursor: disabled ? "not-allowed" : "pointer",
});

const USER_MANUAL_SCOPE = {
    moduleName: "Planning",
    process: "Planning",
    subProcess: "Planning Log"
};
const USER_MANUAL_LIST_API = `${MASTER_API}/user-manual/list`;

function PlanningLog() {
  const navigate = useNavigate();
  const location = useLocation();

  const [showManual, setShowManual] = useState(false);
  const [manualExists, setManualExists] = useState(null);
  const [manualFile, setManualFile] = useState(null);

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productDescription, setProductDescription] = useState("");
  const [toast, setToast] = useState(null); // { message, type }

  const showToast = (message, type = "success", durationMs = 3000) => {
    setToast({ message, type });
    setTimeout(() => setToast(null), durationMs);
  };

  const [dropdownData, setDropdownData] = useState({
    productTypes: [],
    categories: [],
    cableTypes: [],
    codes: [],
    colours: [],
    cores: [],
    nominalCrossSections: [],
    referenceOfStandards: [],
    voltages: [],
    constructions: [],
  });
  const [filterValues, setFilterValues] = useState({});
  // Filters accordion: expanded by default, user can collapse to free up screen space.
  const [filtersOpen, setFiltersOpen] = useState(true);
  const activeFilterCount =
    Object.values(filterValues).filter(Boolean).length + (selectedProduct ? 1 : 0);

  // Resets every filter (top bar + selected product) back to empty.
  const clearAllFilters = () => {
    setFilterValues({});
    setSelectedProduct(null);
    setProductDescription("");
  };
  const [processOptions, setProcessOptions] = useState([]);
  const [products, setProducts] = useState([]);
  const [jobIdOptions, setJobIdOptions] = useState([]);

  // logData = rows for the Planning Log table
  const [logData, setLogData] = useState([]);

  // ---- Pagination (client-side, over whatever logData currently holds) ----
  const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const productOptions = products.map((item) => ({
    value: item.productCode,
    label: item.productCode,
    description: item.productDescription
  }));

  useEffect(() => {
    fetchDropdowns();
  }, []);

  const fetchDropdowns = async () => {
    try {
      const [productRes, processRes, jobIdRes, ...results] = await Promise.all([
        axios.get(`${ORDER_API}/planning/finished-goods-products`),
        axios.get(`${ORDER_API}/planning/processes`),
        axios.get(`${ORDER_API}/planning/mrp-run-log-jobs`),
        ...FILTER_FIELDS.map((f) =>
          axios.get(`${MASTER_API}/configuration/${f.endpoint}`)
        ),
      ]);

      const next = {};
      FILTER_FIELDS.forEach((f, i) => {
        next[f.dataKey] = results[i].data || [];
      });

      setDropdownData(next);
      setProducts(productRes.data);
      setProcessOptions(processRes.data || []);
      setJobIdOptions(jobIdRes.data || []);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    // Job Name / From / To now need to keep filtering even when a Product
    // Code has been picked directly, so both fetch paths are driven from
    // this one effect instead of the Product Code branch opting out.
    if (selectedProduct?.value) {
      fetchProductLog(selectedProduct.value, filterValues);
    } else {
      // No filters selected = filterValues is {} = fetchFilteredLog returns
      // every row. Filters (when the user picks any) just narrow that same
      // call down — nothing needs to be selected for data to show up.
      fetchFilteredLog(filterValues);
    }
    // fetchFilteredLog/fetchProductLog are redefined every render (not
    // memoized) — intentionally not added as deps here, same pattern as
    // ProductPlanning.js.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValues, selectedProduct]);

  const fetchFilteredLog = async (filters) => {
    try {
      const res = await axios.post(`${ORDER_API}/planning/mrp-run-log`, filters);
      setLogData(res.data || []);
    } catch (err) {
      console.error("Failed to fetch planning log", err);
      showToast("Failed to fetch planning log.", "error");
    }
  };

  // jobName / fromDateTime / toDateTime are sent as query params so they
  // keep narrowing the results even once a specific Product Code is chosen.
  const fetchProductLog = async (productCode, filters = {}) => {
    try {
      const { jobName, fromDateTime, toDateTime } = filters;
      const res = await axios.get(`${ORDER_API}/planning/mrp-run-log/${productCode}`, {
        params: { jobName, fromDateTime, toDateTime },
      });
      setLogData(res.data || []);
    } catch (err) {
      console.error("Failed to fetch planning log", err);
      showToast("Failed to fetch planning log.", "error");
    }
  };

  const handleFilterChange = (key, selected) => {
    setFilterValues((prev) => ({ ...prev, [key]: selected?.value || "" }));
  };

  // Plain text/datetime inputs (Job Name, From/To) — not react-select, so
  // they take the raw value straight off the event.
  const handleInputFilterChange = (key, value) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
  };

  // Opens Product Master's own view modal directly via its existing
  // ?view=<code>&returnTo=<path> mechanism (see Product.js openViewModal /
  // closeModal) — closing that modal there navigates back here on its own,
  // no changes needed in Product.js.
  const openProductMaster = (productCode) => {
    if (!productCode) return;
    const returnTo = encodeURIComponent(`${location.pathname}${location.search}`);
    navigate(`/cable/product/product-code?view=${encodeURIComponent(productCode)}&returnTo=${returnTo}`);
  };

  // Whenever the underlying data set changes (new filter/product/search
  // result), jump back to page 1 so the user isn't stuck on a page number
  // that may no longer exist for the new result set.
  useEffect(() => {
    setCurrentPage(1);
  }, [logData]);

  const totalRows = logData.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * pageSize;
  const paginatedLogData = logData.slice(startIndex, startIndex + pageSize);

  return (
    <Layout>
      <div style={{ padding: 20 }}>

        {/* TOAST MESSAGE */}
        {toast && (
          <div
            style={{
              position: "fixed",
              top: 20,
              right: 20,
              zIndex: 9999,
              minWidth: 260,
              maxWidth: 420,
              padding: "12px 16px",
              borderRadius: 6,
              color: "#fff",
              fontSize: 13,
              fontWeight: 500,
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              background: toast.type === "error" ? "#d32f2f" : "#2e7d32",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              animation: "toastSlideIn 0.25s ease-out"
            }}
          >
            <span style={{ whiteSpace: "pre-line" }}>{toast.message}</span>
            <button
              onClick={() => setToast(null)}
              style={{
                background: "transparent",
                border: "none",
                color: "#fff",
                cursor: "pointer",
                fontSize: 16,
                lineHeight: 1,
                padding: 0
              }}
              aria-label="Close"
            >
              ×
            </button>
          </div>
        )}
        <style>{`
          @keyframes toastSlideIn {
            from { transform: translateX(30px); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
        `}</style>

        {/* HEADER */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16
          }}
        >
          <h2 style={pageTitle}>Planning Log</h2>
          <button
            onClick={() => {
              setManualExists(null);
              setManualFile(null);
              setShowManual(true);
              fetch(USER_MANUAL_LIST_API, { headers: authHeaders() })
                .then((res) => res.json())
                .then((data) => {
                  const norm = (v) => String(v || "").trim().toLowerCase();
                  const match = (data?.data || []).find((m) =>
                    norm(m.module_name) === norm(USER_MANUAL_SCOPE.moduleName) &&
                    norm(m.process_name) === norm(USER_MANUAL_SCOPE.process) &&
                    norm(m.sub_process_name) === norm(USER_MANUAL_SCOPE.subProcess)
                  );
                  if (match && match.drive_link) {
                    setManualFile(match);
                    setManualExists(true);
                  } else {
                    setManualExists(false);
                  }
                })
                .catch(() => setManualExists(false));
            }}
            title="View User Manual"
            style={{
              background: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: "10px",
              padding: "10px 14px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              color: "#3b82f6",
              fontSize: "14px",
              fontWeight: "600",
              boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#3b82f6"; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; }}
          >
            <FileText size={18} />
            User Manual
          </button>
        </div>

        {/* --- User Manual Popup Modal --- */}
        {showManual && (
          <div
            onClick={() => setShowManual(false)}
            style={{
              position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
              backgroundColor: "rgba(15, 23, 42, 0.6)", display: "flex",
              alignItems: "center", justifyContent: "center", zIndex: 1100,
            }}
          >
            
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: "#ffffff",
                borderRadius: "12px",
                width: "90vw",
                height: "97vh",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                boxShadow: "0 20px 25px -5px rgba(0,0,0,0.2)",
              }}
            >
              <div style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "14px 20px", borderBottom: "1px solid #e2e8f0",
              }}>
                <h4 style={{ margin: 0, color: "#0f172a", fontSize: "16px", fontWeight: "600" }}>
                  User Manual
                </h4>
                <button
                  onClick={() => setShowManual(false)}
                  style={{
                    border: "none", background: "#f1f5f9", borderRadius: "8px",
                    width: "32px", height: "32px", cursor: "pointer", fontSize: "18px",
                    color: "#475569", lineHeight: 1,
                  }}
                  title="Close"
                >
                  ×
                </button>
              </div>

              {manualExists === null && (
                <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: "14px" }}>
                  Checking for user manual...
                </div>
              )}

              {manualExists === false && (
                <div style={{
                  flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
                  justifyContent: "center", color: "#64748b", gap: "10px", padding: "20px", textAlign: "center"
                }}>
                  <FileText size={40} color="#cbd5e1" />
                  <div style={{ fontSize: "15px", fontWeight: 600, color: "#334155" }}>
                    User Manual Not Available
                  </div>
                  <div style={{ fontSize: "13px", color: "#94a3b8", maxWidth: "320px" }}>
                    The user manual for this module hasn't been uploaded yet. Please check back later or contact your administrator.
                  </div>
                </div>
              )}

              {manualExists === true && manualFile && (() => {
                const MANUAL_SCALE = 1.25;
                return (
                  <div style={{ flex: 1, overflow: "hidden", position: "relative" }}>
                    <iframe
                      src={manualFile.drive_link.replace("/view", "/preview")}
                      title={manualFile.file_name || "User Manual"}
                      allow="autoplay"
                      style={{
                        border: "none",
                        width: `${100 / MANUAL_SCALE}%`,
                        height: `${100 / MANUAL_SCALE}%`,
                        transform: `scale(${MANUAL_SCALE})`,
                        transformOrigin: "top left",
                      }}
                    />
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        <div style={filterCard}>
          {/* Accordion header — click to show / hide the filters */}
          <button
            type="button"
            onClick={() => setFiltersOpen((o) => !o)}
            aria-expanded={filtersOpen}
            aria-controls="filters-body"
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "0 0 10px 0",
              background: "transparent",
              border: "none",
              borderBottom: filtersOpen ? "1px solid #eef0f3" : "none",
              cursor: "pointer",
              textAlign: "left",
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {activeFilterCount > 0 && (
                <span
                  style={{
                    background: "#0d6efd",
                    color: "#fff",
                    borderRadius: 10,
                    padding: "1px 8px",
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  {activeFilterCount} active
                </span>
              )}
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 14 }}>
              {activeFilterCount > 0 && (
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    clearAllFilters();
                  }}
                  style={{
                    padding: "4px 10px",
                    background: "#fff",
                    color: "#c0392b",
                    border: "1px solid #c0392b",
                    borderRadius: 4,
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  Clear Filters
                </span>
              )}
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "5px 12px",
                  background: "#fff",
                  color: "#334155",
                  border: "1px solid #dee2e6",
                  borderRadius: 6,
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {filtersOpen ? "Hide Filters" : "Show Filters"}
                <span
                  style={{
                    display: "inline-block",
                    fontSize: 12,
                    color: "#555",
                    transition: "transform 0.25s ease",
                    transform: filtersOpen ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                >
                  ▼
                </span>
              </span>
            </span>
          </button>

          <div
            id="filters-body"
            style={{
              display: "grid",
              gridTemplateRows: filtersOpen ? "1fr" : "0fr",
              transition: "grid-template-rows 0.3s ease",
            }}
          >
            <div style={{ overflow: filtersOpen ? "visible" : "hidden", minHeight: 0 }}>
              <div style={{ paddingTop: 14 }}>
          {/* FILTERS - Product Type + Process (matches ProductPlanning.js top row) */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(200px, 260px) minmax(200px, 260px)",
              gap: "14px",
              marginBottom: "14px",
              alignItems: "end",
            }}
          >
            {FILTER_FIELDS.filter((f) => f.key === "productType").map((f) => (
              <div key={f.key}>
                <label style={filterLabel}>{f.label}</label>
                <Select
                  options={(dropdownData[f.dataKey] || []).map((item) => ({
                    value: item.name,
                    label: item.name,
                  }))}
                  value={
                    filterValues[f.key]
                      ? { value: filterValues[f.key], label: filterValues[f.key] }
                      : null
                  }
                  onChange={(selected) => handleFilterChange(f.key, selected)}
                  placeholder="Select"
                  isClearable
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
            ))}

            <div>
              <label style={filterLabel}>Process</label>
              <Select
                options={processOptions.map((p) => ({
                  value: p.process_id,
                  label: `${p.process_id} - ${p.process_name}`,
                }))}
                value={
                  filterValues.processId
                    ? {
                      value: filterValues.processId,
                      label:
                        processOptions.find((p) => p.process_id === filterValues.processId)
                          ? `${filterValues.processId} - ${processOptions.find((p) => p.process_id === filterValues.processId).process_name}`
                          : filterValues.processId,
                    }
                    : null
                }
                onChange={(selected) => handleFilterChange("processId", selected)}
                placeholder="Select Process"
                isClearable
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>
          </div>

          {/* Remaining filters in compact box (matches ProductPlanning.js / Pegging.js) */}
          <div style={compactFilterBox}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 8, flexWrap: "wrap" }}>
              {["category", "cableType", "code", "color", "noOfCores", "nominalCrossSection", "referenceOfStandard", "voltage", "construction"]
                .map((key) => FILTER_FIELDS.find((f) => f.key === key))
                .map((f) => (
                  <div
                    key={f.key}
                    style={{
                      minWidth: 120,
                      flex: "1 1 110px",
                      maxWidth: 180,
                      display: "flex",
                      flexDirection: "column",
                      gap: 3,
                    }}
                  >
                    <span style={compactFilterItemLabel}>{f.label}</span>
                    <Select
                      options={(dropdownData[f.dataKey] || []).map((item) => ({
                        value: item.name,
                        label: item.name,
                      }))}
                      value={
                        filterValues[f.key]
                          ? { value: filterValues[f.key], label: filterValues[f.key] }
                          : null
                      }
                      onChange={(selected) => handleFilterChange(f.key, selected)}
                      placeholder="Select"
                      isClearable
                      styles={compactSelectStyles}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>
                ))}
            </div>
          </div>

          {/* PRODUCT CODE + DESCRIPTION + JOB NAME + FROM/TO DATE & TIME — one row */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr",
              gap: "14px"
            }}
          >
            <div>
              <label style={filterLabel}>Product Code</label>
              <Select
                options={productOptions}
                value={selectedProduct}
                onChange={(selected) => {
                  // Just update state here — the [filterValues, selectedProduct]
                  // effect above picks this up and fires the right fetch
                  // (product-scoped when selected, filter-scoped otherwise),
                  // carrying along Job Name / From / To either way.
                  setSelectedProduct(selected);
                  setProductDescription(selected?.description || "");
                  setLogData([]);
                }}
                placeholder="Search Product Code"
                isClearable
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>

            <div>
              <label style={filterLabel}>Product Description</label>
              <input
                type="text"
                value={productDescription}
                readOnly
                placeholder="Product Description"
                style={{
                  width: "100%",
                  height: "32px",
                  border: "1px solid #dee2e6",
                  borderRadius: "4px",
                  padding: "0 10px",
                  fontSize: "13px",
                  background: "#f9f9f9",
                  boxSizing: "border-box",
                }}
              />
            </div>

            <div>
              <label style={filterLabel}>Job Name</label>
              <Select
                options={jobIdOptions.map((item) => ({
                  value: item.job_id,
                  label: item.job_id,
                }))}
                value={
                  filterValues.jobName
                    ? { value: filterValues.jobName, label: filterValues.jobName }
                    : null
                }
                onChange={(selected) => handleFilterChange("jobName", selected)}
                placeholder="Search Job Name / Job ID"
                isClearable
                isSearchable
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>

            <div>
              <label style={filterLabel}>From Date &amp; Time</label>
              <DateTimeInput
                value={filterValues.fromDateTime || ""}
                onChange={(v) => handleInputFilterChange("fromDateTime", v)}
              />
            </div>

            <div>
              <label style={filterLabel}>To Date &amp; Time</label>
              <DateTimeInput
                value={filterValues.toDateTime || ""}
                onChange={(v) => handleInputFilterChange("toDateTime", v)}
              />
            </div>
          </div>
              </div>
            </div>
          </div>
        </div>

        {/* ===================== PLANNING LOG TABLE ===================== */}
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            marginTop: 10,
            tableLayout: "fixed"
          }}
        >
          <thead>
            <tr>
              <th style={{ ...tableHeader, width: "18%" }}>Job ID</th>
              <th style={{ ...tableHeader, width: "20%" }}>Product Code</th>
              <th style={{ ...tableHeader, width: "17%" }}>Run Date</th>
              <th style={{ ...tableHeader, width: "17%" }}>Run Time</th>
              <th style={{ ...tableHeader, width: "18%" }}>MRP Result</th>
            </tr>
          </thead>

          <tbody>
            {paginatedLogData.length === 0 ? (
              <tr>
                <td style={tableCell} colSpan={5}>No records found.</td>
              </tr>
            ) : (
              paginatedLogData.map((item, index) => (
                // job_id alone is NOT unique per row — one job groups many
                // product rows (see the grouping described up top), so
                // using just job_id as the key caused React to confuse
                // rows with the same job_id across page changes and leave
                // stale <tr> elements in the DOM instead of removing them
                // (that's what was showing up as "extra" rows on page 2).
                // job_id + product_code + index is unique per row.
                <tr key={`${item.job_id || "job"}-${item.product_code || selectedProduct?.value || "code"}-${index}`}>
                  <td style={tableCell}>{item.job_id || "-"}</td>
                  <td style={tableCell}>
                    {(item.product_code || selectedProduct?.value) ? (
                      <span
                        onClick={() => openProductMaster(item.product_code || selectedProduct?.value)}
                        style={{
                          color: "#1d4ed8",
                          textDecoration: "underline",
                          cursor: "pointer",
                        }}
                      >
                        {item.product_code || selectedProduct?.value}
                      </span>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td style={tableCell}>{formatRunDate(item.run_date)}</td>
                  <td style={tableCell}>{formatRunTime(item.run_time)}</td>
                  <td style={tableCell}>{item.mrp_result || "-"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* ===================== PAGINATION ===================== */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 12,
            marginTop: 14,
            fontSize: 13,
            color: "#334155",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span>Rows per page:</span>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              style={{
                height: 30,
                borderRadius: 4,
                border: "1px solid #dee2e6",
                fontSize: 13,
                padding: "0 8px",
                background: "#fff",
                cursor: "pointer",
              }}
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
            <span>
              {totalRows === 0
                ? "0 of 0"
                : `${startIndex + 1}-${Math.min(startIndex + pageSize, totalRows)} of ${totalRows}`}
            </span>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <button
              onClick={() => setCurrentPage(1)}
              disabled={safeCurrentPage === 1}
              style={paginationButtonStyle(safeCurrentPage === 1)}
            >
              «
            </button>
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={safeCurrentPage === 1}
              style={paginationButtonStyle(safeCurrentPage === 1)}
            >
              ‹ Prev
            </button>
            <span style={{ padding: "0 6px" }}>
              Page {safeCurrentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={safeCurrentPage === totalPages}
              style={paginationButtonStyle(safeCurrentPage === totalPages)}
            >
              Next ›
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={safeCurrentPage === totalPages}
              style={paginationButtonStyle(safeCurrentPage === totalPages)}
            >
              »
            </button>
          </div>
        </div>

      </div>
    </Layout>
  );
}

export default PlanningLog;